import { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Animated,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  Swords,
  Trophy,
  Clock,
  CheckCircle,
  XCircle,
  Dumbbell,
} from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD, BUTTON, SECTION_LABEL } from '@/constants/designTokens';
import type { Duel } from './DuelCard';

interface EnhancedDuelCardProps {
  duel: Duel;
  currentUserId: string;
  profileById: Record<string, { display_name?: string | null; handle?: string | null }>;
  onPress?: (duel: Duel) => void;
  onAccept?: (duelId: string) => void;
  onDecline?: (duelId: string) => void;
  onCancel?: (duelId: string) => void;
}

function getTimeRemaining(endAt?: string | null): { label: string; urgent: boolean; expired: boolean } {
  if (!endAt) return { label: '--', urgent: false, expired: false };
  const ms = Math.max(0, new Date(endAt).getTime() - Date.now());
  if (ms <= 0) return { label: 'Ended', urgent: false, expired: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const urgent = ms < 86400000; // < 24h
  const label = d > 0 ? `${d}d ${h}h left` : h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  return { label, urgent, expired: false };
}

export default function EnhancedDuelCard({
  duel,
  currentUserId,
  profileById,
  onPress,
  onAccept,
  onDecline,
  onCancel,
}: EnhancedDuelCardProps) {
  const { theme } = useTheme();
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const isChallenger = duel.challenger_id === currentUserId;
  const otherId = isChallenger ? duel.challenged_id : duel.challenger_id;
  const otherProfile = otherId ? profileById[otherId] ?? null : null;
  const otherName = otherProfile?.display_name ?? otherProfile?.handle ?? 'Unknown lifter';

  const isWinner = duel.status === 'completed' && duel.winner_user_id === currentUserId;
  const isLoss = duel.status === 'completed' && duel.winner_user_id != null && duel.winner_user_id !== currentUserId;
  const isDraw = duel.status === 'completed' && !duel.winner_user_id;

  const outcomeColor = isWinner
    ? theme.colors.success
    : isLoss
      ? theme.colors.error
      : isDraw
        ? '#B8860B'
        : theme.colors.border;

  const statusBadge = () => {
    const map: Record<string, { label: string; color: string }> = {
      active: { label: 'ACTIVE', color: theme.colors.success },
      pending: { label: 'PENDING', color: theme.colors.warning },
      completed: { label: isWinner ? 'W' : isLoss ? 'L' : 'D', color: outcomeColor },
      expired: { label: 'EXPIRED', color: theme.colors.textSecondary },
      declined: { label: 'DECLINED', color: theme.colors.textSecondary },
      cancelled: { label: 'CANCELLED', color: theme.colors.textSecondary },
    };
    const s = map[duel.status] ?? { label: duel.status.toUpperCase(), color: theme.colors.textSecondary };
    return (
      <View style={[styles.statusBadge, { backgroundColor: s.color + '22', borderColor: s.color }]}>
        <Text style={[styles.statusText, { color: s.color }]}>{s.label}</Text>
      </View>
    );
  };

  const handlePressIn = () => {
    Animated.spring(scaleAnim, { toValue: 0.97, useNativeDriver: true, speed: 50 }).start();
  };
  const handlePressOut = () => {
    Animated.spring(scaleAnim, { toValue: 1, useNativeDriver: true, speed: 50 }).start();
  };

  const isPendingIncoming = duel.status === 'pending' && duel.challenged_id === currentUserId;
  const isPendingOutgoing = duel.status === 'pending' && duel.challenger_id === currentUserId;

  return (
    <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
      <TouchableOpacity
        activeOpacity={0.8}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onPress={() => onPress?.(duel)}
        style={[
          styles.card,
          {
            backgroundColor: theme.colors.surface,
            borderColor: duel.status === 'completed' ? outcomeColor : theme.colors.border,
          },
        ]}>
        {/* Top row: opponent + status */}
        <View style={styles.topRow}>
          <View style={styles.opponentRow}>
            <View style={[styles.avatar, { backgroundColor: theme.colors.surfaceVariant }]}>
              <Text style={[styles.avatarText, { color: theme.colors.text }]}>
                {otherName.charAt(0).toUpperCase()}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.opponentName, { color: theme.colors.text }]} numberOfLines={1}>
                vs {otherName}
              </Text>
              <View style={styles.exerciseRow}>
                <Dumbbell size={12} color={theme.colors.textSecondary} />
                <Text style={[styles.exerciseText, { color: theme.colors.textSecondary }]}>
                  {duel.exercise_name || duel.lift_type || 'Exercise'} • {duel.duel_type === 'pr' ? 'PR' : duel.duel_type === '1rm' ? '1RM' : duel.duel_type === 'consistency' ? 'Consistency' : duel.duel_type === 'strength' ? 'Strength' : 'Volume'}
                </Text>
              </View>
            </View>
          </View>
          {statusBadge()}
        </View>

        {/* Active: time remaining + mini scores */}
        {duel.status === 'active' && (() => {
          const tr = getTimeRemaining(duel.end_at);
          const timerColor = tr.expired ? '#6b7280' : tr.urgent ? '#ef4444' : theme.colors.warning;
          return (
            <View style={[styles.activeInfo, { backgroundColor: tr.expired ? '#6b728018' : tr.urgent ? '#ef444418' : theme.colors.surfaceVariant }]}>
              <Clock size={14} color={timerColor} />
              <Text style={[styles.activeTime, { color: timerColor }]}>
                {tr.label}
              </Text>
              {duel.challenger_score != null && duel.challenged_score != null && (
                <Text style={[styles.miniScore, { color: theme.colors.text }]}>
                  {Math.round(isChallenger ? duel.challenger_score : duel.challenged_score)} vs{' '}
                  {Math.round(isChallenger ? duel.challenged_score : duel.challenger_score)}
                </Text>
              )}
            </View>
          );
        })()}

        {/* Pending incoming: accept/decline */}
        {isPendingIncoming && onAccept && onDecline && (
          <View style={styles.actions}>
            <Pressable
              style={[styles.acceptBtn, { backgroundColor: theme.colors.success }]}
              onPress={() => onAccept(duel.id)}
              android_ripple={{ color: 'rgba(0,0,0,0.2)', borderless: false }}
              hitSlop={{ top: 8, bottom: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Accept duel">
              <CheckCircle size={16} color={theme.colors.text} />
              <Text style={[styles.actionText, { color: theme.colors.text }]}>Accept</Text>
            </Pressable>
            <Pressable
              style={[styles.declineBtn, { borderColor: theme.colors.error }]}
              onPress={() => onDecline(duel.id)}
              android_ripple={{ color: 'rgba(239,68,68,0.15)', borderless: false }}
              hitSlop={{ top: 8, bottom: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Decline duel">
              <XCircle size={16} color={theme.colors.error} />
              <Text style={[styles.actionText, { color: theme.colors.error }]}>Decline</Text>
            </Pressable>
          </View>
        )}

        {/* Pending outgoing */}
        {isPendingOutgoing && (
          <View style={[styles.pendingInfo, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Clock size={14} color={theme.colors.accent} />
            <Text style={[styles.pendingText, { color: theme.colors.accent, flex: 1 }]}>Awaiting response</Text>
            {onCancel && (
              <TouchableOpacity
                style={[styles.cancelBtn, { borderColor: theme.colors.error }]}
                onPress={() => onCancel(duel.id)}
              >
                <XCircle size={14} color={theme.colors.error} />
                <Text style={[styles.cancelBtnText, { color: theme.colors.error }]}>Cancel</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Completed: score comparison */}
        {duel.status === 'completed' && duel.challenger_score != null && duel.challenged_score != null && (
          <View style={[styles.scoreRow, { backgroundColor: theme.colors.surfaceVariant }]}>
            <Text style={[styles.scoreLabel, { color: theme.colors.text }]}>
              {isWinner ? 'W' : isLoss ? 'L' : 'D'}{' '}
              {Math.round(isChallenger ? duel.challenger_score : duel.challenged_score)} -{' '}
              {Math.round(isChallenger ? duel.challenged_score : duel.challenger_score)}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  card: {
    ...CARD.standard,
    marginBottom: 16,
    gap: 12,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  opponentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: RADIUS.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: FONT.lg,
    fontFamily: TYPO.heading.bold,
  },
  opponentName: {
    fontSize: FONT.base,
    fontFamily: TYPO.body.medium,
  },
  exerciseRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  exerciseText: {
    fontSize: FONT.xs,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
  },
  statusBadge: {
    borderWidth: 1,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: RADIUS.none,
  },
  statusText: {
    fontSize: FONT.xs,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: TRACKING.wide,
  },
  activeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: RADIUS.xs,
  },
  activeTime: {
    fontSize: FONT.sm,
    fontFamily: TYPO.body.bold,
    flex: 1,
  },
  miniScore: {
    fontSize: FONT.base,
    fontFamily: TYPO.heading.bold,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  acceptBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    borderRadius: RADIUS.none,
  },
  declineBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 14,
    borderRadius: RADIUS.none,
    borderWidth: 1,
  },
  actionText: {
    fontSize: FONT.base,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
  },
  pendingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: RADIUS.xs,
  },
  pendingText: {
    fontSize: FONT.sm,
    fontFamily: TYPO.body.semibold,
  },
  scoreRow: {
    padding: 12,
    borderRadius: RADIUS.xs,
    alignItems: 'center',
  },
  scoreLabel: {
    fontSize: FONT.base,
    fontFamily: TYPO.heading.bold,
  },
  cancelBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: RADIUS.none,
    borderWidth: 1,
  },
  cancelBtnText: {
    fontSize: FONT.xs,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
  },
});
